import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth, useAuthModal } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

import { useCoinShop } from '../wallet/index.js';
import { TIP_PRESETS } from './TipModal.js';
import { StepFormChrome } from './StepFormChrome.js';
import './PrivateShowRfpModal.css';

export interface OfflineTipModalProps {
  isOpen: boolean;
  onClose: () => void;
  canTip: boolean;
  coinBalance: number;
  isTipping: boolean;
  tipError?: string | null;
  onSendTip: (amountCoins: number, message?: string) => Promise<boolean>;
}

const STEP_AMOUNT = 0;
const STEP_MESSAGE = 1;

export function OfflineTipModal({
  isOpen,
  onClose,
  canTip,
  coinBalance,
  isTipping,
  tipError = null,
  onSendTip,
}: OfflineTipModalProps): ReactElement | null {
  const { user } = useAuth();
  const { openAuthModal, requireAuth } = useAuthModal();
  const { openCoinShop, requireCoins } = useCoinShop();
  const [step, setStep] = useState(STEP_AMOUNT);
  const [amount, setAmount] = useState('10');
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setStep(STEP_AMOUNT);
    setError(null);
    setSuccess(false);
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);

  function send(): void {
    requireAuth(() => {
      const amountCoins = Number.parseInt(amount, 10);
      if (!Number.isFinite(amountCoins) || amountCoins < 1) {
        setError(t('common.storefront.offlineTipError'));
        setStep(STEP_AMOUNT);
        return;
      }
      if (!requireCoins(coinBalance, amountCoins, () => {
        send();
      })) {
        setStep(STEP_AMOUNT);
        return;
      }
      setError(null);
      setSuccess(false);
      const note = message.trim();
      void onSendTip(amountCoins, note.length > 0 ? note : undefined).then((sent) => {
        if (sent) {
          setSuccess(true);
          setMessage('');
        } else {
          setError(t('common.storefront.offlineTipError'));
        }
      });
    }, 'tip');
  }

  if (!isOpen) {
    return null;
  }

  const parsedAmount = Number.parseInt(amount, 10);
  const amountValid = Number.isFinite(parsedAmount) && parsedAmount >= 1;

  return createPortal(
    <div className="show-room-modal private-rfp-modal" role="dialog" aria-modal="true">
      <button
        type="button"
        className="show-room-modal__backdrop"
        aria-label={t('common.show.privateShowModalClose')}
        onClick={onClose}
      />
      <div className="show-room-modal__panel">
        <header className="show-room-modal__header">
          <h2 className="show-room-modal__title">{t('common.storefront.offlineTipTitle')}</h2>
          <button
            type="button"
            className="show-room-modal__close"
            onClick={onClose}
            aria-label={t('common.show.privateShowModalClose')}
          >
            ×
          </button>
        </header>

        <div className="show-room-modal__body">
          {!user ? (
            <p className="show-room-modal__muted">
              <button
                type="button"
                className="show-room-modal__text-action"
                onClick={() => {
                  openAuthModal({ mode: 'login', intent: 'tip' });
                }}
              >
                {t('common.storefront.offlineTipSignIn')}
              </button>
            </p>
          ) : success ? (
            <p className="private-rfp-modal__success">{t('common.storefront.offlineTipSuccess')}</p>
          ) : (
            <>
              <p className="show-room-modal__muted">{t('common.storefront.offlineTipHint')}</p>

              <StepFormChrome
                step={step}
                totalSteps={2}
                stepLabel={
                  step === STEP_AMOUNT
                    ? t('common.storefront.offlineTipStepAmount')
                    : t('common.storefront.offlineTipStepMessage')
                }
                error={tipError ?? error}
                isSubmitting={isTipping}
                isLastStep={step === STEP_MESSAGE}
                canGoNext={amountValid}
                submitLabel={
                  isTipping
                    ? t('common.storefront.offlineTipSending')
                    : t('common.storefront.offlineTipSend').replace(
                        '{{amount}}',
                        String(Number.isFinite(parsedAmount) ? parsedAmount : 0),
                      )
                }
                onBack={() => {
                  setError(null);
                  setStep(STEP_AMOUNT);
                }}
                onNext={() => {
                  if (!requireCoins(coinBalance, parsedAmount, () => {
                    setError(null);
                    setStep(STEP_MESSAGE);
                  })) {
                    return;
                  }
                  setError(null);
                  setStep(STEP_MESSAGE);
                }}
                onSubmit={send}
              >
                {step === STEP_AMOUNT ? (
                  <>
                    <div className="private-rfp-modal__presets" aria-label={t('common.show.tipPresets')}>
                      {TIP_PRESETS.map((preset) => (
                        <Button
                          key={preset}
                          label={String(preset)}
                          variant="chip"
                          disabled={isTipping}
                          onClick={() => {
                            if (!requireCoins(coinBalance, preset, () => {
                              setAmount(String(preset));
                              setSuccess(false);
                            })) {
                              return;
                            }
                            setAmount(String(preset));
                            setSuccess(false);
                          }}
                        />
                      ))}
                    </div>
                    <label className="private-rfp-modal__field">
                      {t('common.storefront.offlineTipAmount')}
                      <input
                        className="fm-input"
                        type="number"
                        min={1}
                        inputMode="numeric"
                        value={amount}
                        onChange={(event) => {
                          setAmount(event.target.value);
                          setSuccess(false);
                        }}
                        disabled={isTipping}
                      />
                    </label>
                    {canTip && amountValid && coinBalance < parsedAmount ? (
                      <p className="show-room-modal__muted">
                        <button
                          type="button"
                          className="show-room-modal__text-action"
                          onClick={() => {
                            openCoinShop(parsedAmount);
                          }}
                        >
                          {t('common.show.getMoreCoins')}
                        </button>
                      </p>
                    ) : null}
                  </>
                ) : (
                  <>
                    <label className="private-rfp-modal__field">
                      {t('common.storefront.offlineTipMessage')}
                      <input
                        className="fm-input"
                        type="text"
                        maxLength={200}
                        value={message}
                        placeholder={t('common.storefront.offlineTipMessagePlaceholder')}
                        onChange={(event) => {
                          setMessage(event.target.value);
                          setSuccess(false);
                        }}
                        disabled={isTipping}
                      />
                    </label>
                    <ul className="step-form__review">
                      <li>
                        <span>{t('common.storefront.offlineTipAmount')}</span>
                        <strong>{parsedAmount}</strong>
                      </li>
                    </ul>
                  </>
                )}
              </StepFormChrome>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
